/* eslint-disable warp-drive/no-legacy-request-patterns */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Resource from 'linced-app/enums/enrolment-resource';
import type DexRequest from 'linced-app/models/enrolments/nz-moe/dex-request';
import type Person from 'linced-app/models/person';

// RESOURCE_MODELS gives the model name of each status resource type.
const RESOURCE_MODELS: Partial<Record<number, string>> = {
  [Resource.IDENTIFIERS]: 'enrolments/identifier',
  [Resource.ROLL_RETURN]: 'enrolments/rollreturn',
  [Resource.ENROLMENTS]: 'enrolments/enrolment',
};
import type School from 'linced-app/models/school';

// Proto: enrolments.Status.
export default class Status extends Model {
  declare [Type]: 'enrolments/status';

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('string')
  declare date: string | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare requesterId: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @attr('number', { defaultValue: 0 })
  declare resource: number;

  @attr('number', { defaultValue: 0 })
  declare status: number;

  @attr('number', { defaultValue: 0 })
  declare count: number;

  @attr('number', { defaultValue: 0 })
  declare processed: number;

  @array('string')
  declare resourceIds: StatefulArray<string>; // Needs to be mapped to a model based on resource type.

  // resources gives the records of the resource type, or [] until
  // loadResources loads them. The route must call loadResources before render.
  @tracked resources: readonly unknown[] = [];

  // loadResources loads the records of the resource type into resources.
  async loadResources(): Promise<void> {
    const model = RESOURCE_MODELS[this.resource];
    if (!model || isEmpty(this.resourceIds)) {
      return;
    }
    const params = {
      jurisdiction: this.jurisdiction,
      statusId: this.id,
    };
    // TS-STANDIN(store): the resource model name is chosen at run time.
    this.resources = await this.store.query(model as never, params);
  }

  @fragmentArray('enrolments/nz-moe/dex-request')
  declare nzMoe: FragmentArray<DexRequest>;
}
